/**
 * Upload status for the Hub's Uploads and Failed tabs.
 *
 * Uploads tab (HubUploadsTab):
 *   - "Uploading": this pane's own queue while it sends — sent, sending,
 *     waiting, failed (with Retry failed). Gone once the pane closes.
 *   - "Recent uploads": the server's record of each Upload action, so the
 *     counts are there tomorrow and on another machine — how many went in,
 *     how many didn't and why, how parsing is getting on, and the list of
 *     files that didn't go in (shown here, or downloaded as a CSV).
 * Failed tab (HubFailedTab): everything that went wrong in one place —
 *   uploads that failed in this pane, documents that couldn't be parsed, and
 *   files recent uploads didn't get in.
 * HubUploadBanner: one line atop the Documents tab while this pane sends.
 */

import { couldNotReadCause, STATUS_WORD } from "../statusWords";
import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useRef, useState } from "react";
import type { UploadBatch, UploadProblem } from "../../../api/client";
import { triggerDownload } from "../../commands/export";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../../tokens";
import type { DocumentRow } from "../types";
import {
  batchParsing,
  batchParsingSentence,
  batchReportCsv,
  batchSentence,
  batchWhen,
  countLabel,
  libraryTally,
  type LiveUpload,
  PROBLEM_LABEL,
  problemCount,
  reportFileName,
} from "../uploadStatus";

/** Batches shown before "Show N older". */
const BATCHES_SHOWN = 2;
/** Problem rows drawn in the pane; the CSV carries the rest. */
const PROBLEMS_SHOWN = 100;

export interface HubUploadStatusProps {
  libraryName: string;
  /** This pane's queue for the library, or null when it has sent nothing. */
  live: LiveUpload | null;
  batches: readonly UploadBatch[];
  /** Batches this pane is sending right now. */
  liveBatchIds?: ReadonlySet<string>;
  /** The library's rows, to read parsing progress of the files a batch put in. */
  rows: readonly DocumentRow[];
  onRetryFailed?: () => void;
  onDismiss?: (batchId: string) => void;
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "8px 12px 4px",
    fontFamily: fonts.body,
  },
  card: {
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "10px",
    backgroundColor: "var(--surface-raised)",
    padding: "10px 12px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  cardStopped: {
    border: `1px solid ${statusColors.warn}`,
  },
  heading: {
    margin: 0,
    fontSize: "13px",
    fontWeight: 600,
    color: inkColors.ink,
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "wrap",
  },
  sectionLabel: {
    fontFamily: fonts.mono,
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    color: inkColors.ink4,
    margin: "2px 0 0",
  },
  meta: {
    fontFamily: fonts.mono,
    fontSize: "10px",
    color: inkColors.ink4,
    fontWeight: 400,
  },
  bar: {
    height: "6px",
    borderRadius: "3px",
    backgroundColor: surfaceColors.paperDim,
    overflow: "hidden",
    display: "flex",
  },
  barSent: { backgroundColor: brandColors.primary },
  barFailed: { backgroundColor: statusColors.danger },
  stats: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px 12px",
    fontSize: "11px",
    color: inkColors.ink3,
    margin: 0,
    padding: 0,
    listStyle: "none",
  },
  statValue: {
    fontWeight: 600,
    color: inkColors.ink,
    fontVariantNumeric: "tabular-nums",
  },
  statDanger: { color: statusColors.danger },
  text: {
    margin: 0,
    fontSize: "11.5px",
    lineHeight: 1.45,
    color: inkColors.ink2,
  },
  note: {
    margin: 0,
    fontSize: "10.5px",
    lineHeight: 1.4,
    color: inkColors.ink4,
  },
  badge: {
    fontFamily: fonts.mono,
    letterSpacing: "0.3px",
    fontSize: "10px",
    fontWeight: 600,
    padding: "1px 6px",
    borderRadius: "999px",
    backgroundColor: surfaceColors.paperDim,
    color: inkColors.ink3,
  },
  badgeWarn: {
    backgroundColor: "var(--warning-soft)",
    color: "var(--warning)",
  },
  badgeLive: {
    backgroundColor: brandColors.tint,
    color: brandColors.primary,
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px 12px",
  },
  link: {
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: "11px",
    fontWeight: 600,
    color: brandColors.primary,
    ":hover": { textDecoration: "underline" },
    ":focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "2px" },
  },
  problems: {
    maxHeight: "180px",
    overflowY: "auto",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "8px",
    margin: 0,
    padding: 0,
    listStyle: "none",
  },
  problem: {
    display: "flex",
    flexDirection: "column",
    gap: "1px",
    padding: "5px 8px",
    borderBottom: `1px solid ${surfaceColors.line}`,
    fontSize: "11px",
    ":last-child": { borderBottom: "none" },
  },
  problemName: {
    color: inkColors.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  problemWhy: { color: inkColors.ink4, fontSize: "10.5px" },
  problemWithActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: "6px",
  },
  problemText: {
    display: "flex",
    flexDirection: "column",
    gap: "1px",
    flex: 1,
    minWidth: 0,
  },
  rowActions: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    flex: "0 0 auto",
  },
  rowAction: {
    border: `1px solid ${brandColors.mid}`,
    borderRadius: "999px",
    backgroundColor: "var(--surface-raised)",
    padding: "2px 9px",
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: "10.5px",
    fontWeight: 600,
    color: brandColors.primary,
    ":hover": { backgroundColor: brandColors.tint },
    ":disabled": { cursor: "default", opacity: 0.5 },
  },
  rowRemove: {
    border: `1px solid ${surfaceColors.line}`,
    color: inkColors.ink3,
    ":hover": { color: statusColors.danger },
  },
  ask: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    columnGap: "8px",
    fontSize: "10.5px",
    color: statusColors.danger,
  },
  status: {
    display: "block",
    color: inkColors.ink2,
  },
  askButtons: {
    display: "inline-flex",
    gap: "8px",
  },
  problemButton: {
    background: "none",
    border: "none",
    padding: 0,
    textAlign: "left",
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: "11px",
    ":hover": { textDecoration: "underline" },
  },
  empty: {
    margin: "16px 4px",
    fontSize: "11.5px",
    lineHeight: 1.5,
    color: inkColors.ink3,
    textAlign: "center",
  },
  banner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    padding: "6px 12px",
    backgroundColor: brandColors.tint,
    borderBottom: `1px solid ${surfaceColors.line}`,
    fontFamily: fonts.body,
    fontSize: "11px",
    color: inkColors.ink2,
  },
});

/** The Uploads tab: this pane's live upload, then the server's record of recent ones. */
export const HubUploadsTab: React.FC<HubUploadStatusProps> = ({
  libraryName,
  live,
  batches,
  liveBatchIds,
  rows,
  onRetryFailed,
  onDismiss,
}) => {
  const styles = useStyles();
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? batches : batches.slice(0, BATCHES_SHOWN);
  const older = batches.length - shown.length;

  return (
    <section className={styles.root} aria-label="Uploads" data-testid="hub-upload-status">
      {live && <LiveCard live={live} onRetryFailed={onRetryFailed} />}
      {batches.length > 0 && <p className={styles.sectionLabel}>Recent uploads</p>}
      {shown.map((batch) => (
        <BatchCard
          key={batch.batch_id}
          batch={batch}
          libraryName={libraryName}
          sendingHere={liveBatchIds?.has(batch.batch_id) ?? false}
          rows={rows}
          onDismiss={onDismiss}
        />
      ))}
      {older > 0 && (
        <button type="button" className={styles.link} onClick={() => setShowAll(true)}>
          Show {countLabel(older, "older upload")}
        </button>
      )}
      <LibrarySoFar rows={rows} recorded={batches.length > 0} />
    </section>
  );
};

/** The library as a whole, so the tab has history before the first recorded
 *  upload: uploads made before upload records began were never tracked one
 *  by one, but the documents they put in are all here. */
function LibrarySoFar({ rows, recorded }: { rows: readonly DocumentRow[]; recorded: boolean }) {
  const styles = useStyles();
  const t = libraryTally(rows);
  if (t.documents === 0 && recorded) return null;
  const parts = [`${t.parsed.toLocaleString("en-US")} ready`];
  if (t.parsing > 0) parts.push(`${t.parsing.toLocaleString("en-US")} still reading`);
  if (t.failed > 0) parts.push(`${t.failed.toLocaleString("en-US")} couldn't be read (see Failed)`);
  return (
    <>
      <p className={styles.sectionLabel}>{recorded ? "Whole library" : "Earlier uploads"}</p>
      <div className={styles.card} data-testid="hub-library-so-far">
        {t.documents === 0 ? (
          <p className={styles.note}>Nothing has been uploaded to this library yet. Uploads you start show up here.</p>
        ) : (
          <>
            <p className={styles.text}>
              {countLabel(t.documents, "document")} in the library. {parts.join(", ")}.
            </p>
            {!recorded && (
              <p className={styles.note}>
                These went in before uploads were recorded one by one, so there is no per-upload breakdown for them.
                Every upload from now on is listed here with what went in and what didn't.
              </p>
            )}
          </>
        )}
      </div>
    </>
  );
}

/** One line atop the Documents tab while this pane is sending. */
export function HubUploadBanner({ live, onOpen }: { live: LiveUpload; onOpen: () => void }): React.ReactElement {
  const styles = useStyles();
  const done = live.sent + live.failed;
  return (
    <div className={styles.banner} data-testid="hub-upload-banner">
      <span>
        {live.finished ? "Upload finished" : "Uploading"}: {done.toLocaleString("en-US")} of{" "}
        {countLabel(live.total, "file")}
        {live.failed > 0 ? `, ${live.failed.toLocaleString("en-US")} failed` : ""}
      </span>
      <button type="button" className={styles.link} onClick={onOpen}>
        View
      </button>
    </div>
  );
}

export interface HubFailedTabProps {
  libraryName: string;
  rows: readonly DocumentRow[];
  batches: readonly UploadBatch[];
  /** Send failed uploads again — one (by slot id) or all. The file is still in this pane. */
  onRetryUpload?: (clientId?: string) => void;
  /** Take a failed upload out of the list. */
  onRemoveUpload?: (clientId: string) => void;
  /** Ask the library to read a stored file again. */
  onRetryParse?: (row: DocumentRow) => Promise<unknown>;
  /** Delete a document that couldn't be parsed from the library. */
  onRemoveDoc?: (row: DocumentRow) => unknown;
  /** Take a file a recent upload didn't get in off the list. */
  onClearNotIn?: (batchId: string, problem: UploadProblem) => Promise<unknown>;
  /** Send files that didn't go in again, picked from disk; ``items`` are the
   *  list entries they stand for, which come off the list once sent. */
  onResendNotIn?: (files: File[], items: NotInItem[]) => Promise<unknown>;
  onPickDoc?: (row: DocumentRow) => void;
}

export interface NotInItem {
  batch: UploadBatch;
  problem: UploadProblem;
}

/** Files that didn't go in and can be sent again: everything but the ones
 *  held back because the library already has them. */
export function resendable(item: NotInItem): boolean {
  return item.problem.state !== "duplicate";
}

/** Match picked files to the list by name; the rest are left out. */
export function matchPicked(files: readonly File[], items: readonly NotInItem[]) {
  const byName = new Map(items.filter(resendable).map((i) => [i.problem.name, i]));
  const files_: File[] = [];
  const matched: NotInItem[] = [];
  let leftOut = 0;
  for (const file of files) {
    const item = byName.get(file.name);
    if (!item) {
      leftOut++;
      continue;
    }
    byName.delete(file.name);
    files_.push(file);
    matched.push(item);
  }
  return { files: files_, items: matched, leftOut };
}

/** Everything that went wrong, in one place. */
export function failedItems(rows: readonly DocumentRow[], batches: readonly UploadBatch[]) {
  const uploadFailed = rows.filter((r) => r.processing_status === "failed_upload");
  const parseFailed = rows.filter((r) => r.processing_status === "failed_processing");
  // A file this pane is already showing as failed is listed there, not twice.
  const shownHere = new Set(uploadFailed.map((r) => r.filename));
  const inLibrary = new Set(rows.filter((r) => r.doc_id).map((r) => r.filename));
  const seen = new Set<string>();
  const notIn: NotInItem[] = [];
  for (const batch of batches) {
    for (const problem of batch.problems) {
      if (shownHere.has(problem.name) || seen.has(problem.name)) continue;
      // Went in later (a retry or a re-upload): no longer a problem.
      if (problem.state !== "duplicate" && inLibrary.has(problem.name)) continue;
      seen.add(problem.name);
      notIn.push({ batch, problem });
    }
  }
  return { uploadFailed, parseFailed, notIn, total: uploadFailed.length + parseFailed.length + notIn.length };
}

interface FailedItem {
  key: string;
  name: string;
  why: string;
  onOpen?: () => void;
  onRetry?: () => unknown;
  /** Pick this file from disk and send it again. */
  onPick?: (file: File) => unknown;
  /** Removing asks first when ``removeAsks`` says what it will do. */
  onRemove?: () => unknown;
  removeLabel: string;
  removeAsks?: string;
}

/** The Failed tab: uploads that failed here, documents that couldn't be parsed,
 *  and files recent uploads didn't get in — each with Retry where the file is
 *  still somewhere to retry from, and ✕ to take it off the list. */
export const HubFailedTab: React.FC<HubFailedTabProps> = ({
  libraryName,
  rows,
  batches,
  onRetryUpload,
  onRemoveUpload,
  onRetryParse,
  onRemoveDoc,
  onClearNotIn,
  onResendNotIn,
  onPickDoc,
}) => {
  const styles = useStyles();
  const { uploadFailed, parseFailed, notIn, total } = failedItems(rows, batches);
  const [retryingAll, setRetryingAll] = useState(false);
  const [resendNote, setResendNote] = useState<string | null>(null);
  const pickAll = useRef<HTMLInputElement>(null);
  const resendPicked = async (picked: File[]) => {
    if (!onResendNotIn || picked.length === 0) return;
    const { files, items, leftOut } = matchPicked(picked, notIn);
    const parts: string[] = [];
    if (files.length > 0) parts.push(`Sending ${countLabel(files.length, "file")} again.`);
    if (leftOut > 0)
      parts.push(
        `${countLabel(leftOut, "picked file")} ${leftOut === 1 ? "isn't" : "aren't"} on this list and ${leftOut === 1 ? "was" : "were"} left out.`,
      );
    setResendNote(parts.join(" ") || null);
    if (files.length > 0)
      await onResendNotIn(files, items).catch(() => setResendNote("Couldn't send them. Try again."));
  };
  const download = () => {
    const merged: UploadBatch = {
      ...(batches[0] ?? ({} as UploadBatch)),
      problems: notIn.map((n) => n.problem),
    };
    triggerDownload(reportFileName(libraryName, merged), "text/csv", batchReportCsv(merged));
  };
  const retryAllParse = async () => {
    if (!onRetryParse) return;
    setRetryingAll(true);
    try {
      // One at a time: thousands of parallel reads would swamp the library.
      for (const row of parseFailed) await onRetryParse(row).catch(() => undefined);
    } finally {
      setRetryingAll(false);
    }
  };
  if (total === 0) {
    return (
      <section className={styles.root} aria-label="Failed" data-testid="hub-failed-tab">
        <p className={styles.empty}>Nothing has failed. Every upload went in and every document is ready.</p>
      </section>
    );
  }
  return (
    <section className={styles.root} aria-label="Failed" data-testid="hub-failed-tab">
      {uploadFailed.length > 0 && (
        <FailedGroup
          title={`${STATUS_WORD.didNotUpload} · ${uploadFailed.length.toLocaleString("en-US")}`}
          note="These never reached the library. This pane still has the files, so they can be sent again."
          action={
            onRetryUpload ? (
              <button
                type="button"
                className={styles.link}
                onClick={() => onRetryUpload()}
                data-testid="hub-failed-retry"
              >
                Retry all {uploadFailed.length.toLocaleString("en-US")}
              </button>
            ) : null
          }
          items={uploadFailed.map((r) => ({
            key: r.client_id ?? r.filename,
            name: r.filename,
            why: r.upload_error ?? "Upload failed.",
            onRetry: onRetryUpload && r.client_id ? () => onRetryUpload(r.client_id) : undefined,
            onRemove: onRemoveUpload && r.client_id ? () => onRemoveUpload(r.client_id as string) : undefined,
            removeLabel: "Take out of this upload",
          }))}
        />
      )}
      {parseFailed.length > 0 && (
        <FailedGroup
          title={`${STATUS_WORD.couldNotRead} · ${parseFailed.length.toLocaleString("en-US")}`}
          note="These are stored in the library, but drafts can't cite them until they parse. Retry asks the library to read the stored file again."
          action={
            onRetryParse ? (
              <button type="button" className={styles.link} onClick={retryAllParse} disabled={retryingAll}>
                {retryingAll ? "Retrying…" : `Retry all ${parseFailed.length.toLocaleString("en-US")}`}
              </button>
            ) : null
          }
          items={parseFailed.map((r) => ({
            key: r.doc_id ?? r.filename,
            name: r.filename,
            why: couldNotReadCause(r.upstream_status),
            onOpen: onPickDoc ? () => onPickDoc(r) : undefined,
            onRetry: onRetryParse ? () => onRetryParse(r) : undefined,
            onRemove: onRemoveDoc ? () => onRemoveDoc(r) : undefined,
            removeLabel: "Delete from the library",
            removeAsks: "Delete this file from the library?",
          }))}
        />
      )}
      {notIn.length > 0 && (
        <FailedGroup
          title={`Didn't go in · ${notIn.length.toLocaleString("en-US")}`}
          note="From earlier uploads. These never reached the library, so nothing is stored to retry from: pick the files again to send them. Picked files that aren't on this list are left out, and so are files already in the library."
          action={
            <>
              {onResendNotIn && notIn.some(resendable) && (
                <>
                  <button
                    type="button"
                    className={styles.link}
                    onClick={() => pickAll.current?.click()}
                    data-testid="hub-failed-resend"
                  >
                    Upload these again
                  </button>
                  <input
                    ref={pickAll}
                    type="file"
                    multiple
                    hidden
                    aria-label="Pick the files that didn't go in"
                    onChange={(e) => {
                      const picked = Array.from(e.target.files ?? []);
                      e.target.value = "";
                      void resendPicked(picked);
                    }}
                  />
                </>
              )}
              <button type="button" className={styles.link} onClick={download}>
                Download list
              </button>
            </>
          }
          status={resendNote}
          items={notIn.map((item) => ({
            key: `${item.batch.batch_id}-${item.problem.key}`,
            name: item.problem.name,
            why: problemWhy(item.problem),
            onPick: onResendNotIn && resendable(item) ? (file: File) => onResendNotIn([file], [item]) : undefined,
            onRemove: onClearNotIn ? () => onClearNotIn(item.batch.batch_id, item.problem) : undefined,
            removeLabel: "Take off this list",
          }))}
        />
      )}
    </section>
  );
};

function FailedGroup({
  title,
  note,
  action,
  status,
  items,
}: {
  title: string;
  note?: string;
  action?: React.ReactNode;
  /** One line on what the last group action did. */
  status?: string | null;
  items: FailedItem[];
}): React.ReactElement {
  const styles = useStyles();
  const listed = items.slice(0, PROBLEMS_SHOWN);
  return (
    <div className={styles.card}>
      <h3 className={styles.heading}>{title}</h3>
      {note && <p className={styles.note}>{note}</p>}
      {action && <div className={styles.actions}>{action}</div>}
      {status && <output className={mergeClasses(styles.note, styles.status)}>{status}</output>}
      {/* biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable list must be reachable by keyboard */}
      <ul className={styles.problems} aria-label={title} tabIndex={0}>
        {listed.map((item) => (
          <FailedRow key={item.key} item={item} />
        ))}
        {items.length > listed.length && (
          <li className={styles.problem}>
            <span className={styles.problemWhy}>…and {countLabel(items.length - listed.length, "more file")}.</span>
          </li>
        )}
      </ul>
    </div>
  );
}

function FailedRow({ item }: { item: FailedItem }): React.ReactElement {
  const styles = useStyles();
  const [busy, setBusy] = useState<null | "retry" | "remove">(null);
  const pickOne = useRef<HTMLInputElement>(null);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (kind: "retry" | "remove", fn: () => unknown) => {
    setBusy(kind);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : "That didn't work.");
    } finally {
      setBusy(null);
      setAsking(false);
    }
  };
  return (
    <li className={mergeClasses(styles.problem, styles.problemWithActions)}>
      <div className={styles.problemText}>
        {item.onOpen ? (
          <button
            type="button"
            className={mergeClasses(styles.problemName, styles.problemButton)}
            onClick={item.onOpen}
          >
            {item.name}
          </button>
        ) : (
          <span className={styles.problemName}>{item.name}</span>
        )}
        <span className={styles.problemWhy}>{error ?? item.why}</span>
        {asking && item.removeAsks && (
          <span className={styles.ask}>
            <span>{item.removeAsks}</span>
            <span className={styles.askButtons}>
              <button
                type="button"
                className={styles.link}
                onClick={() => item.onRemove && run("remove", item.onRemove)}
              >
                Delete
              </button>
              <button type="button" className={styles.link} onClick={() => setAsking(false)}>
                Cancel
              </button>
            </span>
          </span>
        )}
      </div>
      <div className={styles.rowActions}>
        {item.onRetry && (
          <button
            type="button"
            className={styles.rowAction}
            onClick={() => item.onRetry && run("retry", item.onRetry)}
            disabled={busy !== null}
            aria-label={`Retry ${item.name}`}
          >
            {busy === "retry" ? "…" : "Retry"}
          </button>
        )}
        {item.onPick && (
          <>
            <button
              type="button"
              className={styles.rowAction}
              onClick={() => pickOne.current?.click()}
              disabled={busy !== null}
              aria-label={`Upload ${item.name} again`}
              title="Pick this file and send it again"
            >
              {busy === "retry" ? "…" : "Upload"}
            </button>
            <input
              ref={pickOne}
              type="file"
              hidden
              aria-label={`Pick ${item.name}`}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file && item.onPick) void run("retry", () => item.onPick?.(file));
              }}
            />
          </>
        )}
        {item.onRemove && (
          <button
            type="button"
            className={mergeClasses(styles.rowAction, styles.rowRemove)}
            onClick={() => (item.removeAsks ? setAsking(true) : item.onRemove && run("remove", item.onRemove))}
            disabled={busy !== null}
            aria-label={`${item.removeLabel}: ${item.name}`}
            title={item.removeLabel}
          >
            ✕
          </button>
        )}
      </div>
    </li>
  );
}

function LiveCard({ live, onRetryFailed }: { live: LiveUpload; onRetryFailed?: () => void }): React.ReactElement {
  const styles = useStyles();
  const done = live.sent + live.failed;
  const pct = (n: number) => `${live.total === 0 ? 0 : (n / live.total) * 100}%`;
  return (
    <div className={styles.card} data-testid="hub-upload-live">
      <h3 className={styles.heading}>
        {live.finished ? "Upload finished" : "Uploading"}
        <span className={styles.meta}>
          {done.toLocaleString("en-US")} of {countLabel(live.total, "file")}
        </span>
      </h3>
      {/* biome-ignore lint/a11y/useFocusableInteractive: a two-segment bar (sent, failed) that <progress> can't draw; it is read, not operated */}
      <div
        className={styles.bar}
        role="progressbar"
        aria-label="Upload progress"
        aria-valuemin={0}
        aria-valuemax={live.total}
        aria-valuenow={done}
      >
        <div className={styles.barSent} style={{ width: pct(live.sent) }} />
        <div className={styles.barFailed} style={{ width: pct(live.failed) }} />
      </div>
      <ul className={styles.stats}>
        <li>
          <span className={styles.statValue}>{live.sent.toLocaleString("en-US")}</span> sent
        </li>
        {live.sending > 0 && (
          <li>
            <span className={styles.statValue}>{live.sending.toLocaleString("en-US")}</span> sending
          </li>
        )}
        {live.waiting > 0 && (
          <li>
            <span className={styles.statValue}>{live.waiting.toLocaleString("en-US")}</span> waiting
          </li>
        )}
        {live.failed > 0 && (
          <li>
            <span className={mergeClasses(styles.statValue, styles.statDanger)}>
              {live.failed.toLocaleString("en-US")}
            </span>{" "}
            failed
          </li>
        )}
      </ul>
      {live.failed > 0 && onRetryFailed && (
        <div className={styles.actions}>
          <button type="button" className={styles.link} onClick={onRetryFailed} data-testid="hub-upload-retry">
            Retry {countLabel(live.failed, "failed file")}
          </button>
        </div>
      )}
      {!live.finished && (
        <p className={styles.note}>Keep this pane open until it finishes. Files still waiting stop if it closes.</p>
      )}
    </div>
  );
}

function BatchCard({
  batch,
  libraryName,
  sendingHere,
  rows,
  onDismiss,
}: {
  batch: UploadBatch;
  libraryName: string;
  sendingHere: boolean;
  rows: readonly DocumentRow[];
  onDismiss?: (batchId: string) => void;
}): React.ReactElement {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const notIn = problemCount(batch);
  const parsing = batchParsingSentence(batchParsing(batch, rows));
  const listed = batch.problems.slice(0, PROBLEMS_SHOWN);
  return (
    <div
      className={mergeClasses(styles.card, batch.stopped && styles.cardStopped)}
      data-testid={`hub-upload-batch-${batch.batch_id}`}
    >
      <h3 className={styles.heading}>
        {batchWhen(batch.started_at)}
        {batch.actor && <span className={styles.meta}>by {batch.actor}</span>}
        {sendingHere && <span className={mergeClasses(styles.badge, styles.badgeLive)}>Sending from this pane</span>}
        {batch.stopped && <span className={mergeClasses(styles.badge, styles.badgeWarn)}>Upload stopped</span>}
      </h3>
      <p className={styles.text}>{batchSentence(batch)}</p>
      {parsing && <p className={styles.note}>{parsing}</p>}
      {batch.stopped && (
        <p className={styles.note}>
          Nothing has moved for an hour, so whatever was sending these files closed. Upload the ones never sent again.
        </p>
      )}
      <div className={styles.actions}>
        {batch.problems.length > 0 && (
          <button
            type="button"
            className={styles.link}
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            data-testid="hub-upload-show-problems"
          >
            {open ? "Hide" : "Show"} {countLabel(notIn, "file")} that didn't go in
          </button>
        )}
        {batch.problems.length > 0 && (
          <button
            type="button"
            className={styles.link}
            onClick={() => triggerDownload(reportFileName(libraryName, batch), "text/csv", batchReportCsv(batch))}
          >
            Download list
          </button>
        )}
        {onDismiss && !sendingHere && (
          <button type="button" className={styles.link} onClick={() => onDismiss(batch.batch_id)}>
            Dismiss
          </button>
        )}
      </div>
      {open && (
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable list must be reachable by keyboard
        <ul className={styles.problems} aria-label="Files that didn't go in" tabIndex={0}>
          {listed.map((p) => (
            <li key={p.key} className={styles.problem}>
              <span className={styles.problemName} title={p.folder ? `${p.folder}/${p.name}` : p.name}>
                {p.name}
              </span>
              <span className={styles.problemWhy}>
                {PROBLEM_LABEL[p.state]}
                {p.message ? `: ${p.message}` : ""}
                {p.folder ? ` · ${p.folder}` : ""}
              </span>
            </li>
          ))}
          {batch.problems.length > listed.length && (
            <li className={styles.problem}>
              <span className={styles.problemWhy}>
                …and {countLabel(batch.problems.length - listed.length, "more file")}. Download the list for all of
                them.
              </span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

export default HubUploadsTab;

/** "Failed: timed out · listings", without repeating a message that only restates the label. */
function problemWhy(problem: UploadProblem): string {
  const label = PROBLEM_LABEL[problem.state];
  const message = problem.message?.replace(/\.$/, "").trim();
  const plain = (text: string) => text.toLowerCase().replace(/\bthe\s+/g, "");
  const restates = message !== undefined && plain(message).includes(plain(label));
  const head = message ? (restates ? message : `${label}: ${message}`) : label;
  return `${head}${problem.folder ? ` · ${problem.folder}` : ""}`;
}
